import React, { useMemo } from 'react';
import { Avatar, Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import { Typography } from '../../../../../components';
import { ProposalData } from '../../types';
import { useTranslations } from '../../../../../contexts/translation.context';
import { CalendarIcon } from '../../../../../components/icons';
import { PartnerInfoResponse } from '../../../../../api/client/clientCplService';

interface StatRowConfig {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: React.ReactNode;
}

const UsersIcon = () => (
  <Icon
    icon="solar:users-group-two-rounded-linear"
    width={16}
    height={16}
    className="text-auth-muted"
  />
);
const MessageIcon = () => (
  <Icon icon="solar:chat-round-line-linear" width={16} height={16} className="text-auth-muted" />
);
const PieChartIcon = () => (
  <Icon icon="solar:pie-chart-2-linear" width={16} height={16} className="text-auth-muted" />
);
const ChecklistIcon = () => (
  <Icon
    icon="solar:checklist-minimalistic-linear"
    width={16}
    height={16}
    className="text-auth-muted"
  />
);
const CheckCircleIcon = () => (
  <Icon icon="solar:check-circle-linear" width={16} height={16} className="text-auth-muted" />
);

const formatNumber = (value?: number | null, isLoading?: boolean) => {
  if (value === null || value === undefined) return isLoading ? '—' : '0';
  return value.toLocaleString();
};

const formatPercent = (value?: number | null) => {
  if (value === null || value === undefined) return '—';
  return `${value}%`;
};

const getTimeAgo = (dateString: string, t: TranslateFn) => {
  // Если сервер отдает локальное время с 'Z', нужно добавить offset
  const utcDate = new Date(dateString);
  if (Number.isNaN(utcDate.getTime())) {
    return '—';
  }
  const date = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000);

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  if (diffMs < 0) {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  const diffInMinutes = Math.floor(diffMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInMonths = Math.floor(diffInDays / 30);

  if (diffInMonths > 0) {
    const remainingDays = diffInDays % 30;
    const template = t('proposals.timeAgo.monthsDays');
    return template
      .replace('{{months}}', diffInMonths.toString())
      .replace('{{days}}', remainingDays.toString());
  }

  if (diffInDays > 0) {
    const template = t('proposals.timeAgo.daysHours');
    return template
      .replace('{{days}}', diffInDays.toString())
      .replace('{{hours}}', (diffInHours % 24).toString());
  }

  if (diffInHours > 0) {
    const template = t('proposals.timeAgo.hours');
    return template.replace('{{hours}}', diffInHours.toString());
  }

  if (diffInMinutes > 0) {
    const template = t('proposals.timeAgo.minutes');
    return template.replace('{{minutes}}', diffInMinutes.toString());
  }

  return t('proposals.timeAgo.justNow');
};

type TranslateFn = (key: string) => string;

const getPartnerLevelStyle = (rating: any, t: TranslateFn) => {
  if (rating === 'new')
    return {
      bg: 'bg-success/20',
      text: 'text-success',
      label: t('proposals.partnerLevels.new'),
    };
  if (rating === 'base')
    return {
      bg: 'bg-[#EEEEEF]',
      text: 'text-auth-muted',
      label: t('proposals.partnerLevels.base'),
    };
  if (rating === 'pro')
    return { bg: 'bg-auth-ink', text: 'text-white', label: t('proposals.partnerLevels.pro') };
  if (rating === 'expert')
    return {
      bg: 'bg-accent/20',
      text: 'text-accent',
      label: t('proposals.partnerLevels.expert'),
    };
  return { bg: 'bg-[#EEEEEF]', text: 'text-auth-muted', label: t('proposals.partnerLevels.base') };
};

const StatRow: React.FC<StatRowConfig> = ({ icon, label, value, hint }) => (
  <div className="flex items-center w-full">
    <div className="flex items-center gap-2 flex-1">
      {icon}
      <span className="text-xs font-normal text-auth-muted leading-4">{label}</span>
      {hint}
    </div>
    <span className="text-xs font-normal text-auth-ink leading-4">{value}</span>
  </div>
);

interface PartnerTabProps {
  proposal: ProposalData;
  onClose: () => void;
  partnerInfo: PartnerInfoResponse | null;
  isLoading: boolean;
}

const PartnerTab: React.FC<PartnerTabProps> = ({ proposal, onClose, partnerInfo, isLoading }) => {
  const { t } = useTranslations();

  const ordersStats = partnerInfo?.orders_stats;
  const subscribers = ordersStats?.total_subscribed_count;
  const transactions = ordersStats?.total_transactions_count;
  const waitingOrders = ordersStats?.waiting ?? null;
  const inProgressOrders = ordersStats?.in_progress ?? null;
  const rejectedOrders = ordersStats?.canceled ?? null;

  const successRate = useMemo(() => {
    if (!ordersStats) return null;
    if (!ordersStats.total) return 0;
    return Math.round((ordersStats.completed / ordersStats.total) * 100);
  }, [ordersStats]);

  const formattedSuccessRate = successRate !== null ? formatPercent(successRate) : '—';
  const waitingValue = formatNumber(waitingOrders, isLoading);
  const inProgressValue = formatNumber(inProgressOrders, isLoading);
  const completedPercent = formattedSuccessRate;
  const rejectedValue = formatNumber(rejectedOrders, isLoading);
  const completedPercentLabel = `${t('proposals.partnerDetails.completed')}`;
  const orderLegendLabels = [
    t('proposals.partnerDetails.orders'),
    t('proposals.partnerDetails.waiting'),
    t('proposals.partnerDetails.inProgress'),
    completedPercentLabel,
    t('proposals.partnerDetails.rejected'),
  ];

  const partnerLevel = getPartnerLevelStyle(partnerInfo?.lvl || proposal.partner.lvl, t);

  const statsRows: StatRowConfig[] = [
    {
      icon: <UsersIcon />,
      label: t('proposals.partnerDetails.subscribers'),
      value: formatNumber(subscribers, isLoading),
    },
    {
      icon: <MessageIcon />,
      label: t('proposals.partnerDetails.messageConversion'),
      value: formatPercent(undefined),
    },
    {
      icon: <PieChartIcon />,
      label: t('proposals.partnerDetails.purchases'),
      value: formatNumber(transactions, isLoading),
    },
    // {
    //   icon: <CheckCircleIcon />,
    //   label: t('proposals.partnerDetails.activeOffers'),
    //   value: formatNumber(partnerInfo?.active_offers, isLoading),
    // },
  ];

  return (
    <div className="flex flex-col">
      {/* Partner Info */}
      <div className="border-b border-auth-surface-hover px-4 py-3">
        <div className="flex items-start gap-4 pl-2">
          <Avatar
            src={proposal.partner.avatar}
            showFallback
            name={proposal.partner.name?.slice(0, 1)}
            className="w-10 h-10 rounded-3"
          />
          <div className="flex flex-col gap-1 justify-center">
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium text-partner cursor-pointer hover:underline leading-5">
                {proposal.partner.name}
              </span>
              <div
                className={`px-2 py-2.5 rounded-full h-6 flex items-center justify-center text-xs font-normal leading-4 ${partnerLevel.bg} ${partnerLevel.text}`}
              >
                {partnerLevel.label}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <CalendarIcon />
              <span className="text-xs font-normal text-auth-muted leading-4">
                {getTimeAgo(proposal.updatedAt, t)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="border-b border-[#E2EBF7] px-4 py-4 flex flex-col gap-4 items-end justify-center">
        <div className="w-full">
          <Typography variant="body2" className="font-medium text-auth-ink text-sm leading-5">
            {t('proposals.partnerDetails.generalStats')}
          </Typography>
        </div>

        <div className="flex flex-col gap-2 w-full">
          {statsRows.map((row) => (
            <StatRow key={row.label} {...row} />
          ))}

          {/* Orders */}
          <div className="flex items-center w-full">
            <div className="flex items-center gap-1.5 flex-1 flex-wrap">
              <ChecklistIcon />
              {orderLegendLabels.map((label, index) => (
                <React.Fragment key={label}>
                  {index > 0 && (
                    <span className="bg-neutral-8 w-[0.1875rem] h-[0.1875rem] rounded-full" />
                  )}
                  <span
                    className={`text-xxs font-normal leading-4 ${index === 0 ? 'text-auth-muted text-xs' : 'text-neutral-8'}`}
                  >
                    {label}
                  </span>
                </React.Fragment>
              ))}
            </div>
            <div className="text-xs font-normal text-auth-ink leading-4 flex items-center gap-1.5">
              <span className="text-auth-muted">{waitingValue}</span>
              <span className="text-auth-strength-none">/</span>
              <span className="text-auth-strength-strong font-medium">{inProgressValue}</span>
              <span className="text-auth-strength-none">/</span>
              <span className="text-auth-muted">{completedPercent}</span>
              <span className="text-auth-strength-none">/</span>
              <span className="text-auth-strength-weak font-medium">{rejectedValue}</span>
            </div>
          </div>

          {/* Active Offers */}
          <div className="flex items-center w-full">
            <div className="flex items-center gap-2 flex-1">
              <CheckCircleIcon />
              <span className="text-xs font-normal text-auth-muted leading-4">
                {t('proposals.partnerDetails.activeOffers')}
              </span>
            </div>
            <span className="text-xs font-normal text-auth-ink leading-4">
              {formatNumber(partnerInfo?.active_offers)}
            </span>
          </div>
        </div>

        {/* Close Button */}
        <Button
          onPress={onClose}
          className="bg-primary text-white h-8 px-3 rounded-2 text-xs font-normal leading-4"
        >
          {t('proposals.partnerDetails.close')}
        </Button>
      </div>
    </div>
  );
};

export default PartnerTab;
